import { createApi } from '@reduxjs/toolkit/query/react';

import { setAppNotice } from '@/app/store/appShellSlice';
import type { ApiResponseBase } from '@/services/types/api';
import dynamicBaseQueryWithLogger from '@/services/utils/dynamicBaseQueryWithLogger';
import { handlerErrorResponse, handleUnwrapRTKError } from '@/services/utils/handlers';

const BASE_URL =
  process.env.NEXT_PUBLIC_PAYMENTS_API_URL ?? 'http://localhost:8082';

export type PaymentMethod = {
  id: number;
  company_id?: number;
  code: string;
  name: string;
  is_active: boolean;
  created_at?: string;
  updated_at?: string;
};

export type PaymentMethodSummary = {
  payment_method_id: number;
  payment_method_code: string;
  payment_method_name: string;
  total_amount: number;
  tendered_amount: number;
  change_amount: number;
  count: number;
};

export type PaymentMethodSummaryResponse = {
  data: {
    items: PaymentMethodSummary[];
  };
};

type PaymentsSummaryByMethodArgs = {
  cash_session_ids: string | Array<number | string>;
};

export type CashSessionMethodNetItem = {
  cash_session_id: number;
  cash_net_amount: number;
};

export type CashSessionMethodNetSummaryResponse = {
  data: {
    payment_method_code?: string;
    items: CashSessionMethodNetItem[];
  };
};

type PaymentsSummaryByMethodBySessionArgs = {
  cash_session_ids: string | Array<number | string>;
  payment_method_code?: string;
  group_by?: 'session';
};

export type PaymentsSummarySnapshot = {
  payment_summary_id: string;
  cash_sales: number;
  cash_refunds: number;
  card_sales: number;
  transfer_sales: number;
  currency_code?: string | null;
  generated_at?: string | null;
};

export type PaymentsSummarySnapshotResponse = {
  data: PaymentsSummarySnapshot;
};

type PaymentsSummaryByCashSessionArgs = {
  cash_session_ids: string | Array<number | string>;
};

export type PaymentMethodPayload = {
  code: string;
  name: string;
  is_active?: boolean;
};

export type PaymentStatus = 'pending' | 'completed' | 'void' | 'refunded';

export type PaymentResponse = {
  id: number;
  source_type: string;
  source_id: number;
  service_order_id?: number; // Compatibilidad con forma legacy
  company_id?: number;
  branch_id?: number;
  payment_method_id?: number;
  amount: number;
  status?: PaymentStatus;
  payment_date: string;
  is_deposit?: boolean;
  notes?: string | null;
  reference?: string | null;
  tendered_amount?: number | null;
  change_amount?: number | null;
  created_at?: string;
  updated_at?: string;
};

export type PaymentListResponse = {
  data: PaymentResponse[];
  pagination: { page: number; page_size: number; total: number };
};

export type PaymentsRevenueResponse = {
  total_amount: number;
  from: string;
  to: string;
  timezone?: string;
};

/**
 * Se define el periodo permitido para resumenes de pagos.
 */
export type PaymentsSummaryPeriod = 'today' | 'week' | 'month';

/**
 * Se describe el agregado por origen para resumenes de pagos.
 */
export type PaymentsSummaryBySourceItem = {
  source_type: string;
  source_id: number;
  payments_count: number;
  total_paid: number;
  total_refunded: number;
  net_amount: number;
};

/**
 * Se define la respuesta agregada por origen con paginacion.
 */
export type PaymentsSummaryBySourceResponse = {
  items: PaymentsSummaryBySourceItem[];
  total: number;
  page: number;
  page_size: number;
  from?: string;
  to?: string;
  timezone?: string;
};

/**
 * Se definen los filtros del resumen de pagos por origen.
 */
export type PaymentsSummaryBySourceQuery = {
  source_type?: string;
  page?: number;
  page_size?: number;
  from?: string;
  to?: string;
  period?: PaymentsSummaryPeriod;
  date?: string;
  timezone?: string;
};

const normalizePaymentsSummaryBySourceResponse = (
  response: unknown,
): PaymentsSummaryBySourceResponse => {
  const payload = response as {
    data?: PaymentsSummaryBySourceResponse | { data?: PaymentsSummaryBySourceResponse };
  };
  const nested = payload?.data;
  const base = nested && typeof nested === 'object' && 'data' in nested ? nested.data : nested;
  const raw = (base ?? response) as Partial<PaymentsSummaryBySourceResponse> | undefined;

  return {
    items: raw?.items ?? [],
    total: raw?.total ?? 0,
    page: raw?.page ?? 1,
    page_size: raw?.page_size ?? 20,
    from: raw?.from,
    to: raw?.to,
    timezone: raw?.timezone,
  };
};

export type PaymentRequest = {
  source_type: string;
  source_id: number;
  amount: number;
  payment_date_iso: string;
  payment_method_id?: number;
  payment_method_code?: string;
  is_deposit?: boolean;
  notes?: string;
  reference?: string;
  tendered_amount?: number;
  change_amount?: number;
};

export type RefundRequest = {
  source_type: string;
  source_id: number;
  amount: number;
  payment_date_iso: string;
  refund_method_id?: number;
  refund_method_code?: string;
  notes?: string;
  reference?: string;
};

export type PaymentsQueryFilters = {
  page?: number;
  page_size?: number;
  status?: PaymentStatus;
  method_id?: number;
  source_type?: string;
  source_id?: number;
  service_order_id?: number;
  date_from?: string;
  date_to?: string;
};

type ApiResponsePayment = ApiResponseBase & { data: PaymentResponse };
type ApiResponsePaymentList = ApiResponseBase & { data: PaymentListResponse };

export const paymentApi = createApi({
  reducerPath: 'paymentApi',
  baseQuery: dynamicBaseQueryWithLogger(BASE_URL),
  tagTypes: ['PaymentMethods', 'Payments', 'PaymentsRevenue', 'PaymentsSummaryBySource', 'PaymentSummaries'],
  endpoints: (builder) => ({
    // GET /v1/payments
    getPayments: builder.query<ApiResponsePaymentList, PaymentsQueryFilters | void>({
      query: (args) => ({
        url: '/v1/payments',
        method: 'GET',
        params: args ?? { page: 1, page_size: 20 },
      }),
      transformErrorResponse: handlerErrorResponse('Payments.List'),
      providesTags: ['Payments'],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          const e = handleUnwrapRTKError(err);
          dispatch(
            setAppNotice({
              message: e?.message || 'Error al listar pagos',
              status: e?.code ?? null,
              details: `Endpoint: ${e?.endpointName ?? 'Payments.List'}${
                e?.code ? ` — Código: ${e.code}` : ''
              }`,
              messageFormatted: '',
              action: 'payments.list',
              endpointName: e?.endpointName || 'Payments.List',
              validationErrors: (e as any)?.details ?? (e as any)?.originalError?.errors ?? {},
            }),
          );
        }
      },
    }),
    // GET /v1/payments/cash-sessions/methods/summary
    getPaymentsSummaryByMethod: builder.query<PaymentMethodSummary[], PaymentsSummaryByMethodArgs>({
      query: ({ cash_session_ids }) => ({
        url: '/v1/payments/cash-sessions/methods/summary',
        method: 'GET',
        params: {
          cash_session_ids: Array.isArray(cash_session_ids)
            ? cash_session_ids.join(',')
            : cash_session_ids,
        },
      }),
      transformResponse: (response: PaymentMethodSummaryResponse) =>
        response?.data?.items ?? [],
      transformErrorResponse: handlerErrorResponse('Payments.SummaryByMethod'),
      providesTags: ['PaymentSummaries'],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          const e = handleUnwrapRTKError(err);
          dispatch(
            setAppNotice({
              message: e?.message || 'Error al obtener resumen por método',
              status: e?.code ?? null,
              details: `Endpoint: ${e?.endpointName ?? 'Payments.SummaryByMethod'}${
                e?.code ? ` — Código: ${e.code}` : ''
              }`,
              messageFormatted: '',
              action: 'payments.summaryByMethod',
              endpointName: e?.endpointName || 'Payments.SummaryByMethod',
              validationErrors: (e as any)?.details ?? (e as any)?.originalError?.errors ?? {},
            }),
          );
        }
      },
    }),
    // GET /v1/payments/cash-sessions/methods/summary?group_by=session
    getPaymentsSummaryByMethodBySession: builder.query<
      CashSessionMethodNetItem[],
      PaymentsSummaryByMethodBySessionArgs
    >({
      query: ({ cash_session_ids, payment_method_code, group_by = 'session' }) => ({
        url: '/v1/payments/cash-sessions/methods/summary',
        method: 'GET',
        params: {
          cash_session_ids: Array.isArray(cash_session_ids)
            ? cash_session_ids.join(',')
            : cash_session_ids,
          group_by,
          ...(payment_method_code ? { payment_method_code } : {}),
        },
      }),
      transformResponse: (response: CashSessionMethodNetSummaryResponse) =>
        (response?.data?.items ?? [])
          .map((item) => ({
            cash_session_id: Number(item?.cash_session_id ?? 0),
            cash_net_amount: Number(item?.cash_net_amount ?? 0),
          }))
          .filter(
            (item) =>
              Number.isFinite(item.cash_session_id) &&
              item.cash_session_id > 0 &&
              Number.isFinite(item.cash_net_amount),
          ),
      transformErrorResponse: handlerErrorResponse('Payments.SummaryByMethodBySession'),
      providesTags: ['PaymentSummaries'],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          const e = handleUnwrapRTKError(err);
          dispatch(
            setAppNotice({
              message: e?.message || 'Error al obtener resumen de efectivo por sesión',
              status: e?.code ?? null,
              details: `Endpoint: ${e?.endpointName ?? 'Payments.SummaryByMethodBySession'}${
                e?.code ? ` — Código: ${e.code}` : ''
              }`,
              messageFormatted: '',
              action: 'payments.summaryByMethodBySession',
              endpointName: e?.endpointName || 'Payments.SummaryByMethodBySession',
              validationErrors: (e as any)?.details ?? (e as any)?.originalError?.errors ?? {},
            }),
          );
        }
      },
    }),
    // GET /v1/payments/cash-sessions/summary
    getPaymentsSummaryByCashSession: builder.query<PaymentsSummarySnapshot | null, PaymentsSummaryByCashSessionArgs>({
      query: ({ cash_session_ids }) => ({
        url: '/v1/payments/cash-sessions/summary',
        method: 'GET',
        params: {
          cash_session_ids: Array.isArray(cash_session_ids)
            ? cash_session_ids.join(',')
            : cash_session_ids,
        },
      }),
      transformResponse: (response: PaymentsSummarySnapshotResponse) =>
        response?.data ?? null,
      transformErrorResponse: handlerErrorResponse('Payments.SummaryCashSession'),
      providesTags: ['PaymentSummaries'],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          const e = handleUnwrapRTKError(err);
          dispatch(
            setAppNotice({
              message: e?.message || 'Error al obtener resumen de caja',
              status: e?.code ?? null,
              details: `Endpoint: ${e?.endpointName ?? 'Payments.SummaryCashSession'}${
                e?.code ? ` — Código: ${e.code}` : ''
              }`,
              messageFormatted: '',
              action: 'payments.summaryCashSession',
              endpointName: e?.endpointName || 'Payments.SummaryCashSession',
              validationErrors: (e as any)?.details ?? (e as any)?.originalError?.errors ?? {},
            }),
          );
        }
      },
    }),
    // GET /v1/payments/revenue
    getPaymentsRevenue: builder.query<PaymentsRevenueResponse, { from?: string; to?: string; period?: string; date?: string; timezone?: string }>({
      query: ({ from, to, period, date, timezone }) => {
        const params: Record<string, string> = {};
        if (from) params.from = from;
        if (to) params.to = to;
        if (period) params.period = period;
        if (date) params.date = date;
        if (timezone) params.timezone = timezone;
        return {
          url: '/v1/payments/revenue',
          method: 'GET',
          params,
        };
      },
      transformResponse: (response: any) => {
        // Se devuelve siempre el payload plano independientemente de si viene envuelto.
        if (response?.data?.data) return response.data.data as PaymentsRevenueResponse;
        if (response?.data) return response.data as PaymentsRevenueResponse;
        return response as PaymentsRevenueResponse;
      },
      providesTags: ['PaymentsRevenue'],
      transformErrorResponse: handlerErrorResponse('Payments.Revenue'),
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          const e = handleUnwrapRTKError(err);
          dispatch(
            setAppNotice({
              message: e?.message || 'Error al obtener ingresos del rango',
              status: e?.code ?? null,
              details: `Endpoint: ${e?.endpointName ?? 'Payments.Revenue'}${
                e?.code ? ` — Código: ${e.code}` : ''
              }`,
              messageFormatted: '',
              action: 'payments.revenue',
              endpointName: e?.endpointName || 'Payments.Revenue',
              validationErrors: (e as any)?.details ?? (e as any)?.originalError?.errors ?? {},
            }),
          );
        }
      },
    }),
    // GET /v1/payments/sources/summary
    getPaymentsSummaryBySource: builder.query<
      PaymentsSummaryBySourceResponse,
      PaymentsSummaryBySourceQuery
    >({
      query: ({ source_type, page, page_size, from, to, period, date, timezone }) => {
        const params: Record<string, string> = {};
        if (source_type) params.source_type = source_type;
        if (page) params.page = String(page);
        if (page_size) params.page_size = String(page_size);
        if (from) params.from = from;
        if (to) params.to = to;
        if (period) params.period = period;
        if (date) params.date = date;
        if (timezone) params.timezone = timezone;
        return {
          url: '/v1/payments/sources/summary',
          method: 'GET',
          params,
        };
      },
      transformResponse: (response: unknown) => normalizePaymentsSummaryBySourceResponse(response),
      providesTags: ['PaymentsSummaryBySource'],
      transformErrorResponse: handlerErrorResponse('Payments.SummaryBySource'),
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          const e = handleUnwrapRTKError(err);
          dispatch(
            setAppNotice({
              message: e?.message || 'Error al obtener resumen por orden',
              status: e?.code ?? null,
              details: `Endpoint: ${e?.endpointName ?? 'Payments.SummaryBySource'}${
                e?.code ? ` — Código: ${e.code}` : ''
              }`,
              messageFormatted: '',
              action: 'payments.summary.by_source',
              endpointName: e?.endpointName || 'Payments.SummaryBySource',
              validationErrors: (e as any)?.details ?? (e as any)?.originalError?.errors ?? {},
            }),
          );
        }
      },
    }),
    // GET /v1/payment-methods
    getPaymentMethods: builder.query<PaymentMethod[], void>({
      query: () => ({ url: '/v1/payment-methods', method: 'GET' }),
      transformErrorResponse: handlerErrorResponse('Payment.Methods'),
      providesTags: ['PaymentMethods'],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          const e = handleUnwrapRTKError(err);
          dispatch(
            setAppNotice({
              message: e?.message || 'Error al listar los metodos disponibles',
              status: e?.code ?? null,
              details: `Endpoint: ${e?.endpointName ?? 'Payment.Methods'}${
                e?.code ? ` — Código: ${e.code}` : ''
              }`,
              messageFormatted: '',
              action: 'payment.method.list',
              endpointName: e?.endpointName || 'Payment.Methods',
              validationErrors: (e as any)?.details ?? (e as any)?.originalError?.errors ?? {},
            }),
          );
        }
      },
    }),
    // POST /v1/payment-methods
    registerPaymentMethod: builder.mutation<PaymentMethod, PaymentMethodPayload>({
      query: (body) => ({ url: '/v1/payment-methods', method: 'POST', body }),
      transformErrorResponse: handlerErrorResponse('Payment.Methods.Create'),
      invalidatesTags: ['PaymentMethods'],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          const e = handleUnwrapRTKError(err);
          dispatch(
            setAppNotice({
              message: e?.message || 'Error al crear el método de pago',
              status: e?.code ?? null,
              details: `Endpoint: ${e?.endpointName ?? 'Payment.Methods.Create'}${
                e?.code ? ` — Código: ${e.code}` : ''
              }`,
              messageFormatted: '',
              action: 'payment.method.create',
              endpointName: e?.endpointName || 'Payment.Methods.Create',
              validationErrors: (e as any)?.details ?? (e as any)?.originalError?.errors ?? {},
            }),
          );
        }
      },
    }),
    // PUT /v1/payment-methods/{id}
    updatePaymentMethod: builder.mutation<
      PaymentMethod,
      { id: number | string; body: PaymentMethodPayload }
    >({
      query: ({ id, body }) => ({ url: `/v1/payment-methods/${id}`, method: 'PUT', body }),
      transformErrorResponse: handlerErrorResponse('Payment.Methods.Update'),
      invalidatesTags: ['PaymentMethods'],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          const e = handleUnwrapRTKError(err);
          dispatch(
            setAppNotice({
              message: e?.message || 'Error al actualizar el método',
              status: e?.code ?? null,
              details: `Endpoint: ${e?.endpointName ?? 'Payment.Methods.Update'}${
                e?.code ? ` — Código: ${e.code}` : ''
              }`,
              messageFormatted: '',
              action: 'payment.method.update',
              endpointName: e?.endpointName || 'Payment.Methods.Update',
              validationErrors: (e as any)?.details ?? (e as any)?.originalError?.errors ?? {},
            }),
          );
        }
      },
    }),
    // PUT /v1/payment-methods/{id}/active
    updatePaymentMethodActive: builder.mutation<
      PaymentMethod,
      { id: number | string; is_active: boolean }
    >({
      query: ({ id, is_active }) => ({
        url: `/v1/payment-methods/${id}/active`,
        method: 'PUT',
        body: { is_active },
      }),
      transformErrorResponse: handlerErrorResponse('Payment.Methods.Active'),
      invalidatesTags: ['PaymentMethods'],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          const e = handleUnwrapRTKError(err);
          dispatch(
            setAppNotice({
              message: e?.message || 'Error al actualizar estatus del método',
              status: e?.code ?? null,
              details: `Endpoint: ${e?.endpointName ?? 'Payment.Methods.Active'}${
                e?.code ? ` — Código: ${e.code}` : ''
              }`,
              messageFormatted: '',
              action: 'payment.method.active',
              endpointName: e?.endpointName || 'Payment.Methods.Active',
              validationErrors: (e as any)?.details ?? (e as any)?.originalError?.errors ?? {},
            }),
          );
        }
      },
    }),
    // POST /v1/payments
    createPayment: builder.mutation<
      ApiResponsePayment,
      { body: PaymentRequest; idempotencyKey: string }
    >({
      query: ({ body, idempotencyKey }) => ({
        url: '/v1/payments',
        method: 'POST',
        body,
        headers: { 'X-Idempotency-Key': idempotencyKey },
      }),
      transformErrorResponse: handlerErrorResponse('Payments.Create'),
      invalidatesTags: ['Payments', 'PaymentsRevenue'],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          const e = handleUnwrapRTKError(err);
          dispatch(
            setAppNotice({
              message: e?.message || 'Error al registrar el pago',
              status: e?.code ?? null,
              details: `Endpoint: ${e?.endpointName ?? 'Payments.Create'}${
                e?.code ? ` — Código: ${e.code}` : ''
              }`,
              messageFormatted: '',
              action: 'payments.create',
              endpointName: e?.endpointName || 'Payments.Create',
              validationErrors: (e as any)?.details ?? (e as any)?.originalError?.errors ?? {},
            }),
          );
        }
      },
    }),
    // POST /v1/orders/{order_id}/payments
    createOrderPayment: builder.mutation<
      ApiResponsePayment,
      { orderId: number | string; body: PaymentRequest; idempotencyKey: string }
    >({
      query: ({ orderId, body, idempotencyKey }) => ({
        url: `/v1/orders/${orderId}/payments`,
        method: 'POST',
        body,
        headers: { 'X-Idempotency-Key': idempotencyKey },
      }),
      transformErrorResponse: handlerErrorResponse('Orders.Payments'),
      invalidatesTags: ['Payments', 'PaymentsRevenue'],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          const e = handleUnwrapRTKError(err);
          dispatch(
            setAppNotice({
              message: e?.message || 'Error al registrar el pago de la orden',
              status: e?.code ?? null,
              details: `Endpoint: ${e?.endpointName ?? 'Orders.Payments'}${
                e?.code ? ` — Código: ${e.code}` : ''
              }`,
              messageFormatted: '',
              action: 'orders.payments',
              endpointName: e?.endpointName || 'Orders.Payments',
              validationErrors: (e as any)?.details ?? (e as any)?.originalError?.errors ?? {},
            }),
          );
        }
      },
    }),
    // POST /v1/orders/{order_id}/refunds
    createOrderRefund: builder.mutation<
      ApiResponsePayment,
      { orderId: number | string; body: RefundRequest; idempotencyKey: string }
    >({
      query: ({ orderId, body, idempotencyKey }) => ({
        url: `/v1/orders/${orderId}/refunds`,
        method: 'POST',
        body,
        headers: { 'X-Idempotency-Key': idempotencyKey },
      }),
      transformErrorResponse: handlerErrorResponse('Orders.Refunds'),
      invalidatesTags: ['Payments', 'PaymentsRevenue'],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          const e = handleUnwrapRTKError(err);
          dispatch(
            setAppNotice({
              message: e?.message || 'Error al registrar el reembolso',
              status: e?.code ?? null,
              details: `Endpoint: ${e?.endpointName ?? 'Orders.Refunds'}${
                e?.code ? ` — Código: ${e.code}` : ''
              }`,
              messageFormatted: '',
              action: 'orders.refunds',
              endpointName: e?.endpointName || 'Orders.Refunds',
              validationErrors: (e as any)?.details ?? (e as any)?.originalError?.errors ?? {},
            }),
          );
        }
      },
    }),
  }),
});

export type PaymentListItem = PaymentResponse;
export type PaymentMethodItem = PaymentMethod;
export type WarrantyItem = {
  id: number;
  name: string;
  duration_days: number;
  company_id?: number;
  terms?: string | null;
};

export const {
  useGetPaymentsQuery,
  useGetPaymentMethodsQuery,
  useRegisterPaymentMethodMutation,
  useUpdatePaymentMethodActiveMutation,
  useUpdatePaymentMethodMutation,
  useCreatePaymentMutation,
  useGetPaymentsRevenueQuery,
  useGetPaymentsSummaryBySourceQuery,
  useCreateOrderPaymentMutation,
  useCreateOrderRefundMutation,
  useGetPaymentsSummaryByCashSessionQuery,
  useLazyGetPaymentsSummaryByCashSessionQuery,
  useGetPaymentsSummaryByMethodQuery,
  useGetPaymentsSummaryByMethodBySessionQuery,
} = paymentApi;

export type { ApiResponseBase };
